import {
  CertificateStatus,
  capitalizeFirstLetterEachWord,
  localToUtc,
  stringToDate,
  CustomerSummary,
  CustomerStatus,
  getCurrentDateOnly,
} from '@vcd-ecm-ui/Common';
import { GenericGridRowDataTemplate } from '@vcd-ecm-ui/GenericGrid';

// This is the Native Data Type used in the Grid
export declare type GridDataNativeType = CustomerSummary;

// This is the Data for Each Grid Row
export default class GridRowData implements GenericGridRowDataTemplate {
  taxPayerName: string;
  startDate: Date;
  endDate: Date;
  status: CustomerStatus;
  numCertificates: boolean;

  key: string; // Unique Key for Each Row - Required by VtxTable
  rowData: GridDataNativeType;

  constructor(rowData: GridDataNativeType) {
    this.rowData = rowData;
    this.setKey();

    this.setStatus();
    this.setStartDate();
    this.setEndDate();
    this.setTaxpayer();
    this.setExemptions();

    const proxyHandler = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      get: (instance: GridRowData, prop: string): any => ((prop in instance) && ((instance as any)[prop] !== undefined)
        ? (instance as any)[prop] // eslint-disable-line @typescript-eslint/no-explicit-any
        : (rowData as any)[prop]), // eslint-disable-line @typescript-eslint/no-explicit-any
    };

    const proxy = new Proxy(this, proxyHandler);

    return proxy;
  }

  public static fromData(rowData: GridDataNativeType): GridRowData {
    return new GridRowData(rowData);
  }

  private setKey(): void {
    this.key = this.rowData.customerUuid;
  }

  private setStatus(): void {
    let status: CustomerStatus = this.rowData.status || CustomerStatus.Pending;

    // If Approved Status break it down into Active or Inactive
    if (status === CustomerStatus.Approved) {
      // End Date not defined
      if (this.rowData.endDate) {
        const currentDate = getCurrentDateOnly();
        const endDate: Date = stringToDate(this.rowData.endDate);

        // If Today's Date is smaller than the End date the Customer is Active, otherwise it is Inactive
        status = currentDate < endDate ? CustomerStatus.Active : CustomerStatus.Inactive;
      } else {
        status = CustomerStatus.Active;
      }
    }

    this.status = status;
  }

  private setStartDate(): void {
    this.startDate = localToUtc(stringToDate(this.rowData.startDate));
  }

  private setEndDate(): void {
    this.endDate = localToUtc(stringToDate(this.rowData.endDate));
  }

  private setTaxpayer(): void {
    this.taxPayerName = this.rowData.taxpayerDefinition
      ? `${this.rowData.taxpayerDefinition.taxPayerName} / ${this.rowData.taxpayerDefinition.partitionName}`
      : null;
  }

  private setExemptions(): void {
    this.numCertificates = this.rowData ? this.rowData.numCertificates > 0 : false;
  }
}
